import { ReactElement, useState } from 'react'
import { useEffectOnce } from 'react-use'

import {
  deletePersonalezonen,
  getPersonalezonen,
  postPersonalezonen,
  postPersonalezonenAll,
} from '../../api/personalezonen'
import Company from '../../model/company'
import { formatError, isRequestError } from '../../utils/error-utils'
import { t } from '../../utils/translation-utils'
import Alert from '../elements/alert'
import Button from '../elements/button'
import Card from '../elements/card'
import Col from '../elements/grid/col'
import Row from '../elements/grid/row'
import Modal from '../elements/modal'
import Subtitle from '../elements/Subtitle'
import FeatureLock from '../widgets/FeatureLock'
import LoadingOverlay from '../widgets/LoadingOverlay'

import './pzintegration.scss'

type Props = {
  company: Company
  isPremiumCustomer?: boolean
}

export default function PersonalezonenIntr(props: Props): ReactElement | null {
  const [activated, setActivated] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [removeVisibility, setRemoveVisibility] = useState<boolean>(false)
  const [activateAllVisibility, setActivateAllVisibility] = useState<boolean>(false)
  const [activateAllChecked, setActivateAllChecked] = useState<boolean>(false)

  useEffectOnce(() => {
    setLoading(true)
    getPersonalezonen(props.company.id)
      .then((res) => {
        if (!res || !res.data || !res.data.activate) {
          setActivated(false)
        } else {
          setActivated(true)
        }
      })
      .catch((e) => {
        if (isRequestError(e)) {
          setError(e)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  })

  function onActivatrePZ() {
    postPersonalezonen(props.company.id)
      .then(() => {
        setActivated(true)
      })
      .catch((e) => {
        if (isRequestError(e)) {
          setError(e)
        }
      })
  }

  function confirmActivateAllPZ() {
    setActivateAllChecked(false)
    setActivateAllVisibility(true)
  }
  function onActivateAllPZ() {
    setActivateAllVisibility(false)
    postPersonalezonenAll(props.company.id).catch((e) => {
      if (isRequestError(e)) {
        setError(e)
      }
    })
  }

  function cinfirmRemovePZ() {
    setRemoveVisibility(true)
  }
  function onRemovePZ() {
    setRemoveVisibility(false)
    setLoading(true)
    deletePersonalezonen(props.company.id)
      .then(() => {
        setActivated(false)
      })
      .catch((e) => {
        if (isRequestError(e)) {
          setError(e)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  if (loading) {
    return <LoadingOverlay />
  }

  return (
    <Card>
      <Modal
        visible={activateAllVisibility}
        onOk={() => setRemoveVisibility(false)}
        onCancel={() => setActivateAllVisibility(false)}
        width={400}
        footer={null}
      >
        <Card className="pz-activate-all-modal">
          <Subtitle>{t('basic_integrations.personale_zonen.activate_all_title')}</Subtitle>
          <Row>
            <label className="pz-activate-all-modal__label">
              <input
                type="checkbox"
                checked={activateAllChecked}
                onChange={(e) => setActivateAllChecked(e.target.checked)}
              />
              {t('basic_integrations.personale_zonen.activate_all_gdpr_warning')}
            </label>
          </Row>
          <Row>
            <Col span={24}>
              <Button
                type="primary"
                size="extra-extra-large"
                block
                onClick={onActivateAllPZ}
                disabled={!activateAllChecked}
              >
                {t('basic_integrations.personale_zonen.activate_all')}
              </Button>
            </Col>
          </Row>
        </Card>
      </Modal>
      <Modal
        visible={removeVisibility}
        onOk={() => setRemoveVisibility(false)}
        onCancel={() => setRemoveVisibility(false)}
        width={400}
        footer={null}
      >
        <Card>
          <Row>
            <p>{t('basic_integrations.personale_zonen.confirm_text')}</p>
          </Row>
          <Row>
            <Col span={24}>
              <Button danger type="primary" size="extra-extra-large" block onClick={onRemovePZ}>
                {t('basic_integrations.personale_zonen.confirm_remove')}
              </Button>
            </Col>
          </Row>
        </Card>
      </Modal>
      {error && <Alert type="error" message={formatError(error)} />}
      <img
        src="/images/personalezonen-logo-995x175.png"
        alt="PersonaleZonen"
        style={{ width: '40%', height: 'auto', display: 'block', marginBottom: '25px' }}
      />
      <p>{t('basic_integrations.personale_zonen.line_1')}</p>
      <p className="pz-intro-last">{t('basic_integrations.personale_zonen.line_2')}</p>
      {!props.isPremiumCustomer ? (
        <FeatureLock
          featurePackage="Premium"
          description={t('support_tab.phone_support.feature_lock')}
          featureType="Phone Support"
        >
          <Button type="primary" size="extra-extra-large" className="support-tab__help-button">
            {t('basic_integrations.personale_zonen.sign_up')}
          </Button>
        </FeatureLock>
      ) : (
        <>
          {!activated && (
            <Button size="extra-extra-large" type="primary" tabIndex={1} onClick={onActivatrePZ}>
              {t('basic_integrations.personale_zonen.sign_up')}
            </Button>
          )}
          {activated && (
            <>
              <Subtitle style={{ marginBottom: '16px' }}>
                {t('basic_integrations.personale_zonen.is_activated')}
              </Subtitle>
              <div className="pz-action-buttons">
                <Button
                  size="extra-extra-large"
                  type="primary"
                  tabIndex={2}
                  onClick={confirmActivateAllPZ}
                  className="pz-action-button"
                >
                  {t('basic_integrations.personale_zonen.activate_all')}
                </Button>
                <Button
                  size="extra-extra-large"
                  type="primary"
                  danger
                  tabIndex={1}
                  onClick={cinfirmRemovePZ}
                  className="pz-action-button"
                >
                  {t('basic_integrations.personale_zonen.remove')}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  )
}
